import React, { useState } from 'react';
import { Box, Container, useTheme, styled } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginForm from '../components/auth/LoginForm';
import SignupForm from '../components/auth/SignupForm';
import SetPasswordForm from '../components/auth/SetPasswordForm';
import ForgotPasswordForm from '../components/auth/ForgotPasswordForm';

// Styled background with animated gradient
const AnimatedBackground = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(135deg, 
    ${theme.palette.primary.main}20 0%, 
    ${theme.palette.primary.light}15 25%, 
    ${theme.palette.background.default} 50%, 
    ${theme.palette.secondary.light}10 75%, 
    ${theme.palette.secondary.main}15 100%)`,
  animation: 'gradientShift 15s ease infinite',
  '@keyframes gradientShift': {
    '0%, 100%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
  },
  backgroundSize: '200% 200%',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 80%, rgba(144, 191, 69, 0.1) 0%, transparent 50%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 80% 20%, rgba(86, 104, 108, 0.08) 0%, transparent 50%)',
  },
}));

type AuthMode = 'login' | 'signup' | 'setPassword' | 'forgotPassword';

const AuthPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine initial mode from URL path
  const getInitialMode = (): AuthMode => {
    if (location.pathname.includes('signup')) return 'signup';
    if (location.pathname.includes('set-password')) return 'setPassword';
    if (location.pathname.includes('password-reset')) return 'forgotPassword';
    return 'login';
  };

  const [mode, setMode] = useState<AuthMode>(getInitialMode());

  const handleAuthSuccess = (user?: any) => {
    // Redirect based on user role
    // BranchAdmin and SubCustomerAdmin should go to settings, not dashboard
    let defaultPath = '/dashboard';

    if (user) {
      const { AccountType } = require('../types/auth');
      if (user.accountType === AccountType.BranchAdmin || user.accountType === AccountType.SubCustomerAdmin) {
        defaultPath = '/settings';
      }
    }

    const from = (location.state)?.from?.pathname || defaultPath;
    navigate(from, { replace: true });
  };

  const switchToSignup = () => {
    setMode('signup');
    navigate('/auth/signup', { replace: true });
  };

  const switchToLogin = () => {
    setMode('login');
    navigate('/auth/login', { replace: true });
  };

  return (
    <>
      <AnimatedBackground />
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
          zIndex: 1,
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            {/* Floating decorative elements */}
            <Box
              sx={{
                position: 'absolute',
                width: 300,
                height: 300,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${theme.palette.primary.main}10, transparent)`,
                top: -150,
                right: -100,
                filter: 'blur(40px)',
                animation: 'float 6s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-20px)' },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: 250,
                height: 250,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${theme.palette.secondary.main}08, transparent)`,
                bottom: -100,
                left: -80,
                filter: 'blur(30px)',
                animation: 'float 8s ease-in-out infinite reverse',
              }}
            />

            {/* Auth form container */}
            <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
              {(() => {
                if (mode === 'setPassword') {
                  return (
                <SetPasswordForm
                  onSwitchToLogin={switchToLogin}
                />
                  );
                }
                if (mode === 'forgotPassword') {
                  return (
                <ForgotPasswordForm
                  onBackToLogin={switchToLogin}
                />
                  );
                }
                if (mode === 'signup') {
                  return (
                <SignupForm
                  onSwitchToLogin={switchToLogin}
                  onSuccess={handleAuthSuccess}
                />
                  );
                }
                return (
                <LoginForm
                  onSwitchToSignup={switchToSignup}
                  onSuccess={handleAuthSuccess}
                />
                );
              })()}
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default AuthPage;